import * as React from 'react'
import Button from '@mui/material/Button'
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined'
import { Box, Collapse, Dialog, DialogActions, DialogTitle, Divider, FormControl, FormControlLabel, IconButton, InputAdornment, InputLabel, ListSubheader, MenuItem, Radio, RadioGroup, Select, SelectChangeEvent, TextField, Tooltip, Typography } from '@mui/material'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import { ALayer, ALayerInfo, appdata, buckets, defaultALayerInfo, kind, memberKind } from '../internalModules'
import UISelectMaterial from './UISelectMaterial'
import UISelectMember from './UISelectMember'
import { boxStyle, dialogPaperProps, dialogTypographyStyle, halfWidthStyle, rowHeight, textFieldStyle } from '../Graphics'
import UILayerDialogGraphics from './UILayerDialogGraphics'
import UILayerIcon from './UILayerIcon'
import { groupBy } from '../utility'
import UIMemberIcon from './UIMemberIcon'
import UILengthTextField from './UILengthTextField'
import { Dist } from '../Dist'

type Props = {
  assemblyGuid: string,
  bucket: string,
  index: number,
  isOpen: boolean,
  closeDialog: Function
  acceptDialog: Function
}

export default function UILayerDialog({assemblyGuid, bucket, index,
isOpen, closeDialog, acceptDialog}: Props){

  const mode = index < 0 ? 'add' : 'edit'
  const assembly = appdata.getWallType(assemblyGuid)
  const usedLayers = appdata.getUsedLayers(assembly, bucket)
  let layer = new ALayer()
  
  let allowedLayerKinds: string[] = []
  switch (bucket){
    case buckets.wall:
      allowedLayerKinds.push(kind.solid)
      allowedLayerKinds.push(kind.framed)
      break
    case buckets.glazed:
      allowedLayerKinds.push(kind.solid)
      allowedLayerKinds.push(kind.framed)
      break
    case buckets.hSolid:
      allowedLayerKinds.push(kind.solid)
      allowedLayerKinds.push(kind.mDeck)
      break
    case buckets.hFraming:
      allowedLayerKinds.push(kind.framing)
      break
    case buckets.column:
      allowedLayerKinds.push(kind.column)
  }
  let dialogTitle = 'Add New Layer:'
  if (mode === 'edit'){
    const layers = assembly.getLayers(bucket)
    layer = layers[index]
    dialogTitle = 'Edit Layer:'
  }

  const edgeMemberCounts = [0, 1, 2, 3, 4]
  const [layerInfo, setLayerInfo] = React.useState(layer.info)
  const [showHelp, setShowHelp] = React.useState(false)

  const toggleShowHelp = () => {
    setShowHelp(!showHelp)
  }

  const handleChangeSelect = (event: SelectChangeEvent) => {
    const propertyName: string = (event.target as HTMLInputElement).name
    const value: string = (event.target as HTMLInputElement).value
    setLayerInfo({ ...layerInfo, [propertyName]: value})
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement> ) => {
    const propertyName: string = (event.target as HTMLInputElement).name
    const value: string = (event.target as HTMLInputElement).value
    setLayerInfo({ ...layerInfo, [propertyName]: value})
  }

  const handleChangeLength = (propName: string, length: Dist) => {
    setLayerInfo({ ...layerInfo, [propName]: length.in})
  }

  const handleChangeSelectLayer = (event: SelectChangeEvent) => {
    // get selected layer's info as newInfo
    let newLayerInfo = JSON.parse(event.target.value) as ALayerInfo
    if (!newLayerInfo){
      newLayerInfo = layer.info
    }
    setLayerInfo( newLayerInfo )
  }

  const handleClickOk = () => {
    acceptDialog(new ALayer(layerInfo), mode, bucket, index)
    closeDialog()
  }
  const handleClickCancel = () => {
    closeDialog()
  }

  function showLayerSelect() {
    if(layerInfo.kind === ''){
      const grouped = groupBy(usedLayers, ['category'])
      const sortedCategories = Object.keys(grouped).sort( (a,b) => a.localeCompare(b))
      return (
        <Box sx={boxStyle}>
          <Typography sx={dialogTypographyStyle} color='secondary'>Select from layers in project:</Typography>
          <Select
            size='small'
            style={{height: rowHeight}}
            fullWidth
            value=''
            name='usedLayerIndex'
            onChange={handleChangeSelectLayer}
          >
            {sortedCategories.map( category => [
              <ListSubheader color={'primary'} sx={{fontSize:'18px'}} key={category}>{category}</ListSubheader>,
              grouped[category].sort( (a,b) => a.name(assembly).localeCompare(b.name(assembly))).map( (lay, index) => (
                <MenuItem sx={{paddingLeft:4}} key={category + index.toString()} value={JSON.stringify(lay.info)}>
                  <UILayerIcon layerKind={lay.kind} assembly={assembly}/>
                  {lay.name(assembly)}
                </MenuItem>
              ))
            ])}
          </Select>
        </Box>
      )
    }
    else {return (<></>)}
  }

  function showSolidSettings(){
    if (layerInfo.kind === kind.solid) {
      return(
        <Box sx={boxStyle}>
          <UISelectMaterial label='Material:' hideGwpByArea={false} propertyName={'materialGuid'} materialGuid={layerInfo.materialGuid} handleChange={handleChangeSelect}/>
          <UILengthTextField propertyName='thickness' inputPrefix='Thickness:' valueObject={layerInfo} onBlur={handleChangeLength}/>
        </Box>
      )
    }
    else { return(<></>)}
  }

  function showMemberSelect() {
    if(layerInfo.kind === kind.framed || layerInfo.kind === kind.framing || layerInfo.kind === kind.column){
      return (
        <Box sx={boxStyle}>
          <UISelectMember label='Member:' memberGuid={layerInfo.memberGuid} handleChange={handleChangeSelect}
            memberClassFilter={[memberKind.coldFormed,
              memberKind.hollowRect, memberKind.hollowRound,
              memberKind.solidRect, memberKind.solidRound,
              memberKind.wideFlange, memberKind.zee,
            ]}
          />
        </Box>
      )
    }
    else {return (<></>)}
  }

  function showMetalDeckSettings() {
    if(layerInfo.kind === kind.mDeck){
      return (
        <Box sx={boxStyle}>
          <UISelectMember label='Metal Deck Profile:' memberGuid={layerInfo.memberGuid} handleChange={handleChangeSelect}
            memberClassFilter={[memberKind.metalDeck]} />
          <UISelectMaterial label='Deck Fill Material:' hideGwpByArea propertyName='fillMaterialGuid'
            materialGuid={layerInfo.fillMaterialGuid} handleChange={handleChangeSelect}/>
          <UILengthTextField propertyName='fillThickness' inputPrefix='Fill Thickness:' valueObject={layerInfo} onBlur={handleChangeLength} /> 
          <FormControl fullWidth>
            <Select style={textFieldStyle}
              name='framingDirection'
              value={layerInfo.framingDirection}
              onChange={handleChangeSelect}
            >
              <MenuItem sx={{paddingLeft:4}} key={1} value='length'>Span direction: Length</MenuItem>
              <MenuItem sx={{paddingLeft:4}} key={2} value='width'>Span direction: Width</MenuItem>
            </Select>
          </FormControl>
        </Box>
      )
    }
    else {return (<></>)}
  }

  function showFramedSettings() {
    if (layerInfo.kind === kind.framed){
      return (
        <Box sx={boxStyle}>
          <UILengthTextField propertyName='framedVSpacing' inputPrefix='Spacing of Verticals:' valueObject={layerInfo} onBlur={handleChangeLength} />
          <UILengthTextField propertyName='framedHSpacing' inputPrefix='Spacing of Horizontals:' valueObject={layerInfo} onBlur={handleChangeLength} /> 
          <FormControl fullWidth>
            <Select 
              name='framedMembersTop'
              value={layerInfo.framedMembersTop.toString()}
              onChange={handleChangeSelect}
              style={{height: rowHeight}}
            >
              {edgeMemberCounts.map( i => (
                <MenuItem key={i} value={i}>{`${i} Top members`}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={halfWidthStyle}>
            <Select sx={{height: rowHeight}}
              name='framedMembersLeft'
              value={layerInfo.framedMembersLeft.toString()}
              onChange={handleChangeSelect}
            >
              {edgeMemberCounts.map( i => (
                <MenuItem key={i} value={i}>{`${i} Left members`}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={halfWidthStyle}>
            <Select sx={{height: rowHeight}}
              name='framedMembersRight'
              value={layerInfo.framedMembersRight.toString()}
              onChange={handleChangeSelect}
            >
              {edgeMemberCounts.map( i => (
                <MenuItem key={i} value={i}>{`${i} Right members`}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <Select fullWidth
              name='framedMembersBottom'
              value={layerInfo.framedMembersBottom.toString()}
              onChange={handleChangeSelect}
              style={{height: rowHeight, marginBottom: 15}}
            >
              {edgeMemberCounts.map( i => (
                <MenuItem key={i} value={i}>{`${i} Bottom members`}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <UISelectMaterial label='Fill Material:' hideGwpByArea={false} propertyName={'fillMaterialGuid'}
            materialGuid={layerInfo.fillMaterialGuid} handleChange={handleChangeSelect}/>  
          <UILengthTextField propertyName='fillThickness' inputPrefix='Fill Thickness:' valueObject={layerInfo} onBlur={handleChangeLength} />
        </Box>
      )
    }
    else { return (<></>)}
  }

  function showStructuralFramingSettings(){
    if (layerInfo.kind === kind.framing) {
      return(
        <Box sx={boxStyle}>
          <FormControl fullWidth>
            <Select style={textFieldStyle}
              name='framingDirection'
              value={layerInfo.framingDirection}
              onChange={handleChangeSelect}
            >
              <MenuItem sx={{paddingLeft:4}} key={1} value='length'>Span direction: Length</MenuItem>
              <MenuItem sx={{paddingLeft:4}} key={2} value='width'>Span direction: Width</MenuItem>
            </Select>
          </FormControl>
          <UILengthTextField propertyName='framingSpacing' inputPrefix='Intermediate Member Spacing:' valueObject={layerInfo} onBlur={handleChangeLength} />
        </Box>
      )
    }
    else { return(<></>)}
  }

  function showStructuralColumnSettings(){
    if (layerInfo.kind === kind.column) {
      return(
        <Box sx={boxStyle}>
          <FormControl fullWidth>
            <Select style={{height: rowHeight}}
              name='columnHeightRef'
              value={layerInfo.columnHeightRef.toString()}
              onChange={handleChangeSelect}
            >
              <MenuItem sx={{paddingLeft:4}} key={0} value={0}>Height: Custom</MenuItem>
              <MenuItem sx={{paddingLeft:4}} key={1} value={1}>Height: Bottom of framing</MenuItem>
              <MenuItem sx={{paddingLeft:4}} key={2} value={2}>Height: Top of framing</MenuItem>
              <MenuItem sx={{paddingLeft:4}} key={3} value={3}>Height: Top of module</MenuItem>
            </Select>
          </FormControl>
          <UILengthTextField show={layerInfo.columnHeightRef === 0} propertyName='columnHeightDim'
            inputPrefix='Custom Height:' valueObject={layerInfo} onBlur={handleChangeLength} />
        </Box>
      )
    }
    else { return(<></>)}
  }

  function showNotesInput() {
    if (Object.values(kind).includes(layerInfo.kind as kind)){
      return(
        <Box sx={boxStyle}>
          <Typography sx={dialogTypographyStyle} color='secondary'> Optional layer notes:</Typography>
          <TextField multiline
            minRows={2}
            maxRows={10}
            style={{width: '100%'}}
            name='notes'
            value={layerInfo.notes}
            onChange={handleChange}
            placeholder='enter optional notes...'
          />
        </Box>
      )
    }
    else { return (<></>)}
  }

  return(
    <>
      <Dialog
        open={isOpen}
        onClose={handleClickCancel}
        maxWidth='sm'
        fullWidth
        scroll='body'
        PaperProps={dialogPaperProps}
        aria-labelledby="alert-dialog-title"
      >
        <DialogTitle id="alert-dialog-title" fontSize={30} sx={{pl:4}}>
          {dialogTitle}
        </DialogTitle>
          {mode === 'add'?
            <RadioGroup
              value={layerInfo.kind}
              name="kind"
              onChange={handleChange}
              sx={{paddingLeft: 4, paddingBottom: 2}}
              row
            >
              <FormControlLabel
                value={''}
                control={<Radio />}
                label="From Existing" />
              {allowedLayerKinds.includes(kind.solid)?
                <FormControlLabel
                value={kind.solid}
                control={<Radio />}
                label="New Solid Layer" />
              :<></>}
              {allowedLayerKinds.includes(kind.mDeck)?
                <FormControlLabel
                value={kind.mDeck}
                control={<Radio />}
                label="New Metal Deck Layer" />
              :<></>}
              {allowedLayerKinds.includes(kind.framed)?
                <FormControlLabel 
                value={kind.framed}
                control={<Radio />}
                label="New Framed Layer" />
              :<></>}
              {allowedLayerKinds.includes(kind.framing)?
                <FormControlLabel 
                value={kind.framing}
                control={<Radio />}
                label="New Framing Layer" />
              :<></>}
              {allowedLayerKinds.includes(kind.column)?
                <FormControlLabel 
                value={kind.column}
                control={<Radio />}
                label="New Column Layer" />
              :<></>}
          </RadioGroup>
          :<></>
          }
          {showLayerSelect()}
          {showSolidSettings()}
          {showMetalDeckSettings()}
          {showMemberSelect()}
          {showFramedSettings()}
          {showStructuralFramingSettings()}
          {showStructuralColumnSettings()}
          {showNotesInput()}

        <DialogActions>
          <Tooltip arrow title='Toggle help diagram' placement='right'>
            <IconButton sx={{marginRight: '57%', marginBottom: 2}}  onClick={toggleShowHelp} disableRipple>
              <HelpOutlineOutlinedIcon color={showHelp? 'primary': 'disabled'}/>
            </IconButton>
          </Tooltip>
          <Button variant='contained' onClick={handleClickOk} sx={{marginBottom: 2}}>
            Ok
          </Button>
          <Button variant='outlined' onClick={handleClickCancel} sx={{marginRight: 3, marginBottom: 2}}>
            Cancel
          </Button>
        </DialogActions>
        <Collapse in={showHelp} timeout="auto" unmountOnExit>
          <UILayerDialogGraphics layerKind={layerInfo.kind} assembly={assembly}/>
        </Collapse>
      </Dialog>
    </>
    
  )
}